import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { ExtraFieldConfigDTO } from '@/types';

export function useExtensibleFields(tableName: string, companyId?: string) {
  const {
    data: fieldConfigs,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['extraFieldConfigs', tableName, companyId],
    queryFn: () => apiService.getCompanyExtraFieldConfigsByTable(tableName, companyId),
    enabled: !!tableName && !!companyId,
  });

  return {
    fieldConfigs: fieldConfigs || [],
    isLoading,
    error,
    refetch,
  };
}

// Helper function to format field values based on type
export function formatFieldValue(value: any, fieldType: string): string {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  switch (fieldType) {
    case 'Boolean':
      return value ? 'Yes' : 'No';
    case 'Text':
    default:
      return String(value);
  }
}

// Helper function to get field value from extra_fields object
export function getFieldValue(extraFields: Record<string, any> | undefined, fieldName: string): any {
  if (!extraFields) return undefined;
  return extraFields[fieldName];
}

export interface MergedExtraField {
  key: string;
  type: string;
  config?: ExtraFieldConfigDTO;
}

/**
 * Fields to render for a record: every configured field in its configured order, then any keys
 * the record carries that no config covers. Order comes from the config alone — listing
 * populated fields first made a row jump to the top the moment you filled it in.
 */
export function mergeExtraFields(
  extraFields: Record<string, any> | undefined,
  configs: ExtraFieldConfigDTO[],
): MergedExtraField[] {
  const configured = new Set(configs.map((c) => c.name));
  return [
    ...configs.map((c) => ({ key: c.name, type: c.type as string, config: c })),
    ...Object.entries(extraFields || {})
      .filter(([key, value]) => !configured.has(key) && value !== null && value !== undefined)
      .map(([key]) => ({ key, type: 'Text' })),
  ];
}
